import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'

import './styles.scss'
import { FieldWithLabel } from 'components/FieldWithLabel/FieldWithLabel'
import { Select } from 'components/Select'
import { PLACEHOLDERS } from 'constants/txt'
import { TOption } from 'components/Select/types'
import { TResponsibleUser } from 'features/RFP/RFPDetails/types'
import { useRFPDetailsContext } from 'features/RFP/RFPDetails/Providers/RFPDetailsContextProvider'
import { getAvailableResponsibleUsersRequestAsync } from 'features/RFP/RFPDetails/api'
import { useDispatch, useSelector } from 'react-redux'
import { setLoading } from 'redux/store/common/slice'
import { RFP_DETAILS_TABS } from 'features/RFP/RFPDetails/constants'
import { ROLES } from 'features/Permission'
import { CustomForm } from 'components/Forms/CustomForm'
import { useRFPResponsibleContext } from 'features/RFP/RFPDetails/ResponsibleForRFP/Providers/RFPResponsibleContextProvider'
import { getIsLoading } from 'redux/store/common/getters'

export const convertAvailableUsersToOptions = (
  availableUsers: TResponsibleUser[],
  filterByRole: TResponsibleUser['role']
): TOption[] =>
  availableUsers
    .filter((u) => u.role === filterByRole)
    .map((u) => ({
      label: u.first_name + ' ' + u.last_name,
      value: u.uuid
    }))

const generalTab = RFP_DETAILS_TABS[0].key
const biddingTab = RFP_DETAILS_TABS[5].key

type Props = {
  selectedLegalUuid: string | null
  setSelectedLegal: Dispatch<SetStateAction<string | null>>
  selectedAnalystUuid: string | null
  setSelectedAnalyst: Dispatch<SetStateAction<string | null>>
  isCommonsRFP?: boolean
}

const AddResponsibleForRFP = ({
  selectedLegalUuid,
  setSelectedLegal,
  selectedAnalystUuid,
  setSelectedAnalyst,
  isCommonsRFP
}: Props) => {
  const dispatch = useDispatch()
  const isLoading = useSelector(getIsLoading)

  const rfpDetailsContext = useRFPDetailsContext()
  const rfpResponsibleContext = useRFPResponsibleContext()

  const { data, activeTab, isVendor, isCommons } = rfpDetailsContext.state
  const { responsibleUsers } = rfpResponsibleContext.state
  const { addResponsibleUserAsync } = rfpResponsibleContext.actions

  const [availableUsers, setAvailableUsers] = useState<TResponsibleUser[]>([])

  const updateState = (dispatch) => (value) => dispatch(value)

  const availableAnalystUsers = useMemo(() => {
    switch (activeTab) {
      case generalTab:
        return convertAvailableUsersToOptions(
          availableUsers,
          isCommons || isCommonsRFP ? ROLES.COMMONS_ANALYST : ROLES.ANALYST
        )
      case biddingTab:
        return convertAvailableUsersToOptions(
          availableUsers,
          ROLES.VENDOR_ANALYST
        )
      default:
        return []
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, availableUsers])

  const availableLegalUsers = useMemo(() => {
    switch (activeTab) {
      case generalTab:
        return convertAvailableUsersToOptions(
          availableUsers,
          isCommons || isCommonsRFP ? ROLES.COMMONS_LEGAL : ROLES.LEGAL
        )
      case biddingTab:
        return convertAvailableUsersToOptions(
          availableUsers,
          ROLES.VENDOR_LEGAL
        )
      default:
        return []
    }
  }, [activeTab, availableUsers, isCommons, isCommonsRFP])

  const getSelectedAvailableUsers = useCallback(() => {
    switch (activeTab) {
      case generalTab:
        setSelectedAnalyst(
          responsibleUsers.find(
            (u) =>
              u.role ===
              (isCommons || isCommonsRFP
                ? ROLES.COMMONS_ANALYST
                : ROLES.ANALYST)
          )?.uuid ?? null
        )
        setSelectedLegal(
          responsibleUsers.find(
            (u) =>
              u.role ===
              (isCommons || isCommonsRFP ? ROLES.COMMONS_LEGAL : ROLES.LEGAL)
          )?.uuid ?? null
        )
        break
      case biddingTab:
        setSelectedAnalyst(
          responsibleUsers.find((u) => u.role === ROLES.VENDOR_ANALYST)?.uuid ??
            null
        )
        setSelectedLegal(
          responsibleUsers.find((u) => u.role === ROLES.VENDOR_LEGAL)?.uuid ??
            null
        )
        break
      default:
        return []
    }
  }, [
    activeTab,
    isCommons,
    isCommonsRFP,
    responsibleUsers,
    setSelectedAnalyst,
    setSelectedLegal
  ])

  const getAvailableResponsibleUsersAsync = useCallback(async () => {
    if (!data.uuid) {
      throw new Error('RFP ID not provided')
    }

    try {
      dispatch(setLoading(true))

      const response = await getAvailableResponsibleUsersRequestAsync(data.uuid)

      if (response?.data?.length > 0) {
        setAvailableUsers(response?.data)
      }
    } catch (e) {
      console.error(e)
    } finally {
      dispatch(setLoading(false))
    }
  }, [data.uuid, dispatch])

  const handleFinishForm = useCallback(() => {
    const responsibleUsers = [selectedAnalystUuid, selectedLegalUuid]
      .filter((i) => i !== null)
      .map((i) => String(i))

    addResponsibleUserAsync(responsibleUsers)
  }, [addResponsibleUserAsync, selectedAnalystUuid, selectedLegalUuid])

  useEffect(() => {
    getAvailableResponsibleUsersAsync()
  }, [getAvailableResponsibleUsersAsync])

  useEffect(() => {
    getSelectedAvailableUsers()
  }, [getSelectedAvailableUsers])

  return (
    <CustomForm
      id="rfp-add-responsible"
      className="rfp-details-add-responsible"
      onFinish={handleFinishForm}
    >
      {!isLoading && (
        <>
          <FieldWithLabel
            title={`${
              isVendor ? 'Vendor ' : isCommonsRFP ? 'OM Lifeline ' : ''
            }Analyst`}
          >
            <Select
              options={availableAnalystUsers}
              propsSelect={{
                value: selectedAnalystUuid,
                placeholder: PLACEHOLDERS.PLEASE_SELECT,
                onChange: updateState(setSelectedAnalyst)
              }}
            />
          </FieldWithLabel>
          <FieldWithLabel
            title={`${
              isVendor ? 'Vendor ' : isCommonsRFP ? 'OM Lifeline ' : ''
            }Legal`}
          >
            <Select
              options={availableLegalUsers}
              propsSelect={{
                value: selectedLegalUuid,
                placeholder: PLACEHOLDERS.PLEASE_SELECT,
                onChange: updateState(setSelectedLegal)
              }}
            />
          </FieldWithLabel>
        </>
      )}
    </CustomForm>
  )
}

export default AddResponsibleForRFP
